import { useTranslation } from "react-i18next";
import PrimaryButton from "../../../../../../components/common/PrimaryButton";
import styles from "./styles.module.scss";
import AmountCard from "./AmountCard";
import classNames from "classnames";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ContractContext } from "../../../../../../providers/contract";
import TextInput, {
  InputType,
} from "../../../../../../components/forms/textInput";
import { handleCompressImage } from "../../../../../../setup/util/helpers";
import { uploadFile } from "../../../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../../../s3Integration/enums";
import ImageUpload from "../../../../../../components/forms/imageUpload";
import { CircularProgress } from "@mui/material";
import PDFComponent from "../../../../../../components/common/PDFComponent";
import Textarea from "../../../../../../components/forms/textarea";
import { AuthConfig } from "../../../../../../setup/context/config/authConfig";
import {
  AddOnFeeCategory,
  CashOutAmount,
  ESenderRole,
} from "../../../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../../../setup/api/services/contractActions/enum";
import { doAction } from "../../../../../../setup/api/services/contractActions";
import { getShipmentFeesTotal } from "../../../../../../setup/util/contract";
import WaffyFeesSummary from "../components/WaffyFeesSummary";

interface ManualSettlementProps {
  propose?: boolean;
  onSuccess?: () => void;
}

const ManualSettlement = ({ propose, onSuccess }: ManualSettlementProps) => {
  const { t } = useTranslation();
  const { contract, actions } = useContext(ContractContext);

  const userIdentification = AuthConfig.getUserInfo().userIdentification;

  const [sellerValue, setSellerValue] = useState<number | undefined>(undefined);
  const [sellerFee, setSellerFee] = useState<number | undefined>(undefined);
  const [buyerValue, setBuyerValue] = useState<number | undefined>(undefined);
  const [buyerFee, setBuyerFee] = useState<number | undefined>(undefined);
  const [showSettlementError, setShowSettlementError] = useState(false);
  const [showWaffyFeesError, setShowWaffyFeesError] = useState(false);
  const [waffyFeesPercentage, setWaffyFeesPercentage] = useState<number>(
    (contract?.feePercentage ?? 0.04) * 100
  );
  const [description, setDescription] = useState<string>("");
  const [entities, setEntities] = useState<
    {
      file: File;
      s3File: string;
      isLoading: boolean;
      loadingProgress: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);

  const [naqelFees, setNaqelFees] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!contract) return;
    const list = contract.cashOutAmountList;
    const hasList = list?.length;
    const hasWaffyExtra =
      contract.waffyExtraFees?.length &&
      contract.waffyExtraFees.some(
        (f) => f.type === AddOnFeeCategory.SHIPPING_FEES
      );
    if (!hasList && !hasWaffyExtra) return;

    const providerId =
      contract.provider?.id ??
      contract.parties?.find((p) => p.role === ESenderRole.PROVIDER)?.id;

    const customerId =
      contract.customer?.id ??
      contract.parties?.find((p) => p.role === ESenderRole.CUSTOMER)?.id;

    const naqelParty = contract.parties?.find(
      (p) => p.role === ESenderRole.SERVICE_PROVIDER
    );
    const naqelUserId =
      naqelParty?.userId != null ? Number(naqelParty.userId) : undefined;
    const naqelPartyId = naqelParty?.id;

    const isNaqelEntry = (item: CashOutAmount) =>
      item.amountDue != null &&
      (item.id === naqelUserId ||
        item.id === naqelPartyId ||
        (naqelParty?.userId != null &&
          item.userIdentification === String(naqelParty.userId)));

    let naqelPrefilledFromList = false;
    if (list?.length) {
      for (const item of list) {
        const itemAmountDue = item.amountDue;
        const itemFeeBasedOnPercentage =
          item.feePercentage && itemAmountDue
            ? item.feePercentage * itemAmountDue
            : undefined;
        const itemFee = item.fee ?? itemFeeBasedOnPercentage;

        if (item.id === providerId && itemAmountDue != null) {
          setSellerValue(itemAmountDue);
          setSellerFee(itemFee);
        } else if (item.id === customerId && itemAmountDue != null) {
          setBuyerValue(itemAmountDue);
          setBuyerFee(itemFee);
        } else if (isNaqelEntry(item)) {
          setNaqelFees(itemAmountDue);
          naqelPrefilledFromList = true;
        }
      }
      const totalFee = list.reduce((sum, i) => sum + (i.fee ?? 0), 0);
      const totalAmount =
        (contract.originalItemPrice ?? contract.itemPrice ?? 0) +
        getShipmentFeesTotal(contract);
      if (totalAmount > 0 && totalFee >= 0) {
        setWaffyFeesPercentage(
          Math.round((totalFee / totalAmount) * 100 * 100) / 100
        );
      }
    }

    if (
      !naqelPrefilledFromList &&
      hasWaffyExtra &&
      contract.shippable &&
      contract.addonFeesList?.length
    ) {
      const serviceFees = contract.addonFeesList.find(
        (f) => f.type === AddOnFeeCategory.SERVICE_FEES
      );
      if (serviceFees?.amount != null) {
        setNaqelFees(serviceFees.amount);
      }
    }
  }, [contract]);

  const shippingFeeAmount = useMemo(() => {
    if (!contract?.addonFeesList?.length) return 0;
    const shippingFee = contract.addonFeesList.find(
      (fee) => fee.type === AddOnFeeCategory.SHIPPING_FEES
    );
    return shippingFee?.amount || 0;
  }, [contract?.addonFeesList]);

  const shipmentFee = getShipmentFeesTotal(contract);

  const totalAmountDue = useMemo(() => {
    return (
      (contract?.originalItemPrice ?? contract?.itemPrice ?? 0) + shipmentFee
    );
  }, [contract?.itemPrice, contract?.originalItemPrice, shipmentFee]);

  const maxBuyerAmount =
    (contract?.itemPrice ?? 0) + getShipmentFeesTotal(contract);

  const waffyExtraFeesAmount = useMemo(() => {
    if (
      naqelFees === undefined ||
      shippingFeeAmount === 0 ||
      !shippingFeeAmount
    )
      return 0;
    const fees = naqelFees || 0;
    const remaining =
      totalAmountDue - ((buyerValue || 0) + (sellerValue || 0) + fees);
    const maxWaffyExtraFees = shipmentFee - fees;
    const waffyExtraFees = Math.min(remaining, maxWaffyExtraFees);
    return waffyExtraFees >= 0 ? waffyExtraFees : 0;
  }, [
    buyerValue,
    naqelFees,
    sellerValue,
    shipmentFee,
    shippingFeeAmount,
    totalAmountDue,
  ]);

  const buttonDisabled = useCallback(() => {
    if (loading) return true;
    if (!sellerValue && !buyerValue) {
      return true;
    }
    if (
      (sellerValue && sellerFee === undefined) ||
      (buyerValue && buyerFee === undefined) ||
      (!sellerValue && sellerFee) ||
      (!buyerValue && buyerFee)
    ) {
      return true;
    }
    return false;
  }, [sellerValue, sellerFee, buyerValue, buyerFee, loading]);

  const waffyFeesAmount =
    Math.floor(totalAmountDue * waffyFeesPercentage) / 100;

  const handleSettleContract = useCallback(async () => {
    setShowSettlementError(false);
    setShowWaffyFeesError(false);

    if (!contract) return;

    let maxAmount = contract?.itemPrice + shipmentFee;
    if (contract?.originalItemPrice) {
      if ((sellerValue && !buyerValue) || (sellerValue && buyerValue)) {
        maxAmount = contract.originalItemPrice + shipmentFee;
      }
    }

    if (
      Number(
        (
          (sellerValue || 0) +
          (buyerValue || 0) +
          (naqelFees || 0) +
          waffyExtraFeesAmount
        ).toFixed(2)
      ) !== maxAmount
    ) {
      setShowSettlementError(true);
      return;
    }

    if (
      waffyFeesPercentage < 0 ||
      waffyFeesPercentage > 100 ||
      (buyerFee ?? 0) + (sellerFee ?? 0) !== waffyFeesAmount
    ) {
      setShowWaffyFeesError(true);
      return;
    }

    setLoading(true);

    try {
      if (propose) {
        const providerUid = String(
          contract?.partiesProfiles?.PROVIDER?.[0]?.userIdentification ??
            contract?.parties?.find((p) => p.role === ESenderRole.PROVIDER)
              ?.userIdentification
        );
        const customerUid = String(
          contract?.partiesProfiles?.CUSTOMER?.[0]?.userIdentification ??
            contract?.parties?.find((p) => p.role === ESenderRole.CUSTOMER)
              ?.userIdentification
        );

        const proposedSettlement: {
          id: string;
          amountDue: number;
          fee: number;
        }[] = [];

        if (sellerValue !== undefined && sellerFee !== undefined) {
          proposedSettlement.push({
            id: providerUid,
            amountDue: sellerValue - (sellerFee ?? 0),
            fee: 0,
          });
        }
        if (buyerValue !== undefined && buyerFee !== undefined) {
          proposedSettlement.push({
            id: customerUid,
            amountDue: buyerValue - (buyerFee ?? 0),
            fee: 0,
          });
        }

        const brokerUserIdentification = contract?.parties?.find(
          (p) => p.role == ESenderRole.BROKER
        )?.userId;

        if (brokerUserIdentification) {
          proposedSettlement.push({
            id: String(brokerUserIdentification),
            amountDue: (sellerFee ?? 0) + (buyerFee ?? 0),
            fee: 0,
          });
        }

        const resolution =
          (sellerValue || 0) >= (buyerValue || 0)
            ? "RESOLVED_PROVIDER"
            : "RESOLVED_CUSTOMER";

        await doAction({
          contractId: contract?.id,
          userId: String(userIdentification),
          actorRole: ESenderRole.CLIENT_ADMIN,
          actorOrgCode: contract.organizationCode,
          contractAction: EContractActions.CLIENT_RESOLVE_ESCALATION,
          escalationDTO: {
            resolution,
            proposedSettlement,
          },
        } as any);
        setLoading(false);
        actions.getContract();
        onSuccess?.();
      } else {
        await doAction({
          contractId: contract?.id,
          userId: String(userIdentification),
          actorRole: ESenderRole.CLIENT_ADMIN,
          actorOrgCode: contract.organizationCode,
          contractAction: EContractActions.CLIENT_ACCEPT_SETTLEMENT,
        });
        setLoading(false);
        window.location.reload();
      }
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  }, [
    actions,
    buyerFee,
    buyerValue,
    contract,
    naqelFees,
    onSuccess,
    propose,
    sellerFee,
    sellerValue,
    shipmentFee,
    userIdentification,
    waffyExtraFeesAmount,
    waffyFeesAmount,
    waffyFeesPercentage,
  ]);

  const handleFileUpload = useCallback(
    async (e: any) => {
      const tempEntities: {
        file: File;
        s3File: string;
        isLoading: boolean;
        loadingProgress: number;
      }[] = [...entities];
      const allowedFormats = [
        "image/x-png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/png",
        "application/pdf",
      ];
      try {
        const totalFiles =
          e?.target?.files?.length + entities.length > 10
            ? 10 - entities.length
            : e?.target?.files?.length;

        setEntities((prevEntities) => {
          return [
            ...prevEntities,
            ...Array.from(Array(totalFiles).keys()).map(() => ({
              file: undefined as unknown as File,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            })),
          ];
        });

        for (let i = 0; i < totalFiles; i++) {
          const file = e?.target?.files[i];

          if (allowedFormats.indexOf(file?.type) === -1) {
            return;
          }

          if (file?.type !== "application/pdf") {
            tempEntities.push({
              file,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            });

            const image = await handleCompressImage(file);

            if (image) {
              const entryIndex = tempEntities.findIndex(
                (entity) => entity?.file?.name === image?.name
              );

              tempEntities[entryIndex].file = image;
            }
          } else {
            tempEntities.push({
              file,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            });
          }

          if (i === totalFiles - 1) setEntities(tempEntities);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [entities]
  );

  const uploadFiles = useCallback(async () => {
    const entityArray = [...entities];
    const uploadRequests: Promise<void>[] = [];
    for (let i = 0; i < entityArray?.length; i++) {
      const file = entityArray?.[i]?.file;

      if (
        entityArray?.[i]?.file &&
        entityArray?.[i]?.isLoading &&
        entityArray?.[i]?.loadingProgress === 0 &&
        !entityArray?.[i]?.s3File
      ) {
        const uploadRequest = uploadFile(
          file,
          EUploadImageType.CONTRACT,
          "TEMP_WAFFY_SYSTEM",
          (progress) =>
            setEntities((prevEntities) => {
              const newEntities = prevEntities?.map((entity) => {
                if (entity.file.name === file.name) {
                  return {
                    ...entity,
                    loadingProgress: Math.round(
                      (progress?.loaded / progress?.total) * 100
                    ),
                  };
                }
                return entity;
              });
              return newEntities;
            })
        ).then((url) => {
          setEntities((prevEntities) => {
            const newEntities = prevEntities?.map((entity) => {
              if (entity.file.name === file.name) {
                return {
                  file,
                  s3File: url,
                  isLoading: false,
                  loadingProgress: 0,
                };
              }

              return entity;
            });
            return newEntities;
          });
        });

        uploadRequests.push(uploadRequest);
      }
    }

    await Promise.all(uploadRequests);
  }, [entities]);

  useEffect(() => {
    if (
      entities.length > 0 &&
      entities.some(
        (entity) => entity?.isLoading && entity.loadingProgress === 0
      )
    )
      uploadFiles();
  }, [entities, uploadFiles]);

  const handleDeleteFile = useCallback(
    (index: number) => {
      const tempEntities = [...entities];
      tempEntities.splice(index, 1);
      setEntities(tempEntities);
    },
    [entities]
  );

  const handleNaqelFeesChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (value === "") {
        setNaqelFees(undefined);
      } else {
        setNaqelFees(Number(value));
      }
    },
    []
  );

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.error_container}>
          <div className={styles.total_amount_container}>
            <AmountCard
              title={t("lbl_settlement_total_amount")}
              amount={String(totalAmountDue.toFixed(2))}
            />
            <AmountCard
              title={t("lbl_remaining_settlement_amount")}
              amount={
                totalAmountDue
                  ? String(
                      (
                        totalAmountDue -
                        ((sellerValue || 0) +
                          (buyerValue || 0) +
                          (naqelFees || 0) +
                          waffyExtraFeesAmount)
                      ).toFixed(2)
                    )
                  : "0"
              }
              isError={showSettlementError}
              amountColor={showSettlementError ? "problem" : "g900"}
            />
          </div>
          {showSettlementError && (
            <p className={classNames(styles["problem"], styles["error_text"])}>
              {t("lbl_settlement_error")}
            </p>
          )}

          <WaffyFeesSummary
            feeAmount={waffyFeesAmount}
            remainingShippingFeeAmount={waffyExtraFeesAmount}
          />

          {showWaffyFeesError && (
            <p className={classNames(styles["problem"], styles["error_text"])}>
              {t("lbl_waffy_fees_error", {
                waffyFeesAmount: waffyFeesAmount,
              })}
            </p>
          )}

          <div className={styles.sub_section}>
            <h2 className={classNames(styles["subtitle"], styles["h2"])}>
              {t("lbl_waffy_fees")}
            </h2>
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_the_percentage")}
                defaultValue={waffyFeesPercentage}
                maxValue={100}
                type={InputType.NUMBER}
                maxCharacters={5}
                maxDecimals={2}
                handleChange={(e) => {
                  setWaffyFeesPercentage(Number(e.target.value));
                  setShowWaffyFeesError(false);
                }}
                description={t("%")}
                preventMaxValuePass
                placeholder="0.0"
              />
            </div>
          </div>
        </div>
        <div className={styles.sub_section}>
          <h2 className={classNames(styles["subtitle"], styles["h2"])}>
            {t("lbl_the_seller")}
          </h2>
          <div className={styles.total_amount_container}>
            <TextInput
              label={t("lbl_value")}
              maxValue={totalAmountDue}
              type={InputType.NUMBER}
              handleChange={(e) => {
                setSellerValue(Number(e.target.value));
                setShowSettlementError(false);
              }}
              description={t("lbl_saudi_currency")}
              value={sellerValue}
              preventMaxValuePass
              placeholder={t("lbl_enter_value")}
            />
            <div className={styles.half_flex}>
              <TextInput
                label={t("lbl_more_details_sheet_fees")}
                type={InputType.NUMBER}
                maxValue={sellerValue}
                handleChange={(e) => {
                  setSellerFee(
                    e.target.value === "" ? undefined : Number(e.target.value)
                  );
                  setShowSettlementError(false);
                }}
                description={t("lbl_saudi_currency")}
                value={sellerFee}
                preventMaxValuePass
                placeholder={t("lbl_enter_value")}
              />
            </div>
            <TextInput
              label={t("lbl_amount_received")}
              type={InputType.NUMBER}
              description={t("lbl_saudi_currency")}
              disabledValue={
                sellerValue && sellerFee !== undefined
                  ? (sellerValue - sellerFee).toFixed(2)
                  : undefined
              }
              disabled
              placeholder=""
              style={{ color: "#44C16E !important" }}
            />
          </div>
        </div>
        <div className={styles.sub_section}>
          <h2 className={classNames(styles["subtitle"], styles["h2"])}>
            {t("lbl_the_buyer")}
          </h2>
          <div className={styles.total_amount_container}>
            <TextInput
              label={t("lbl_value")}
              maxValue={maxBuyerAmount}
              type={InputType.NUMBER}
              handleChange={(e) => {
                setBuyerValue(Number(e.target.value));
                setShowSettlementError(false);
              }}
              description={t("lbl_saudi_currency")}
              value={buyerValue}
              preventMaxValuePass
              placeholder={t("lbl_enter_value")}
            />
            <div className={styles.half_flex}>
              <TextInput
                label={t("lbl_more_details_sheet_fees")}
                type={InputType.NUMBER}
                maxValue={buyerValue}
                handleChange={(e) => {
                  setBuyerFee(
                    e.target.value === "" ? undefined : Number(e.target.value)
                  );
                  setShowSettlementError(false);
                }}
                description={t("lbl_saudi_currency")}
                value={buyerFee}
                preventMaxValuePass
                placeholder={t("lbl_enter_value")}
              />
            </div>
            <TextInput
              label={t("lbl_amount_received")}
              type={InputType.NUMBER}
              description={t("lbl_saudi_currency")}
              disabledValue={
                buyerValue && buyerFee !== undefined
                  ? (buyerValue - buyerFee).toFixed(2)
                  : undefined
              }
              disabled
              placeholder=""
              style={{ color: "#44C16E !important" }}
            />
          </div>
        </div>

        {contract?.shippable && getShipmentFeesTotal(contract) > 0 && (
          <div className={styles.sub_section}>
            <h2 className={classNames(styles["subtitle"], styles["h2"])}>
              {t("lbl_naqel")}
            </h2>
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_amount_received")}
                value={naqelFees}
                maxValue={shipmentFee}
                type={InputType.NUMBER}
                handleChange={handleNaqelFeesChange}
                description={t("lbl_saudi_currency")}
                preventMaxValuePass
                outerContainerWidth="100%"
                containerWidth="100%"
                placeholder="0.0"
              />
            </div>
          </div>
        )}

        <div className={styles.horizontal_line}></div>
        <div className={styles.info_container}>
          <h2 className={styles.info_title}>
            {t("lbl_contract_details_notes")}
          </h2>
          <Textarea
            label={t("lbl_add_description")}
            required={false}
            value={description}
            maxCharacters={500}
            maxInputHeight={"170px"}
            handleChange={(e) => setDescription(e.target.value)}
            minInputHeight="100px"
          />
          <div className={styles["form-scroll"]}>
            {entities?.map((entity) => entity?.file)?.length < 10 && (
              <div key={0} style={{ marginBottom: "0.2rem" }}>
                <ImageUpload withPDF multiple handleChange={handleFileUpload} />
              </div>
            )}
            {entities?.map((entity, index) => (
              <div key={index} className={styles["uploaded_image_container"]}>
                {entity.isLoading && entity.loadingProgress >= 0 ? (
                  <div
                    className={classNames(
                      styles["uploaded_image"],
                      styles["upload_skeleton"]
                    )}>
                    <CircularProgress
                      {...(!entity?.loadingProgress
                        ? {}
                        : {
                            variant: "determinate",
                            value: entity?.loadingProgress,
                          })}
                    />
                  </div>
                ) : (
                  <>
                    {entity?.file?.type === "application/pdf" ? (
                      <PDFComponent
                        fileUrl={entity?.s3File}
                        fileName={entity?.file?.name}
                        isPreview
                      />
                    ) : (
                      <img
                        className={styles["uploaded_image"]}
                        src={entity?.s3File}
                      />
                    )}
                  </>
                )}
                {entity?.s3File && (
                  <div
                    onClick={() => handleDeleteFile(index)}
                    className={styles["delete_icon"]}>
                    <img src="/assets/images/svg/bin.svg" alt="bin" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.cta_container}>
        <PrimaryButton
          style={{
            backgroundColor: buttonDisabled() ? "#9FA6AB" : "#0D44AD",
            height: "48px",
            color: "#FFFFFF",
            cursor: buttonDisabled() ? "not-allowed" : "pointer",
            fontSize: "1rem",
            fontWeight: 600,
          }}
          onClick={handleSettleContract}
          disabled={buttonDisabled()}>
          {propose ? t("lbl_propose_settlement") : t("lbl_confirm_settlement")}
        </PrimaryButton>
        {/* <PrimaryButton
          style={{
            backgroundColor: "#0C4BC41A",
            height: "48px",
            maxWidth: "234px",
            color: "#0C4BC4",
            fontSize: "16px",
            fontWeight: "400",
          }}>
          {t("lbl_save_for_later")}
        </PrimaryButton> */}
      </div>
    </div>
  );
};

export default ManualSettlement;
